import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './style.css';
import { onFirstInteraction } from '../../utils/firstInteraction';

const desktopImage = `${process.env.PUBLIC_URL}/images/banner/goldcrest-popup-desktop.webp`;
const mobileImage = `${process.env.PUBLIC_URL}/images/banner/goldcrest-popup-mobile.webp`;
const OPEN_DELAY_MS = 1000;
const FALLBACK_MS = 10000;

const STORAGE_KEY = 'goldcrestBannerShown';

const hasBeenShown = () => {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
        return false;
    }
};

const markAsShown = () => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
        // Storage can be blocked (private mode); the banner will simply show again.
    }
};

const GoldcrestBanner = () => {
    // Shown once per browser session: navigating to other pages and coming back
    // to home will not show it again. Closing the tab/browser and reopening will.
    // Opens 1s after first interaction, or 10s after load if the visitor never interacts,
    // so the popup never competes with Lighthouse's first paint measurement.
    const [isOpen, setIsOpen] = useState(false);

    const close = () => setIsOpen(false);

    useEffect(() => {
        if (hasBeenShown()) {
            return undefined;
        }

        let openTimer;
        let fallbackTimer;
        let opened = false;

        const openNow = () => {
            if (opened) return;
            opened = true;
            window.clearTimeout(openTimer);
            window.clearTimeout(fallbackTimer);
            setIsOpen(true);
        };

        const scheduleOpenAfterInteraction = () => {
            if (opened) return;
            window.clearTimeout(openTimer);
            openTimer = window.setTimeout(openNow, OPEN_DELAY_MS);
        };

        const unsubscribe = onFirstInteraction(scheduleOpenAfterInteraction);

        const startFallback = () => {
            fallbackTimer = window.setTimeout(openNow, FALLBACK_MS);
        };

        if (document.readyState === 'complete') {
            startFallback();
        } else {
            window.addEventListener('load', startFallback, { once: true });
        }

        return () => {
            unsubscribe();
            window.removeEventListener('load', startFallback);
            window.clearTimeout(openTimer);
            window.clearTimeout(fallbackTimer);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            markAsShown();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="goldcrest-popup"
            role="dialog"
            aria-modal="true"
            aria-label="Goldcrest Views promotion"
            onClick={close}
        >
            <div className="goldcrest-popup__inner" onClick={(event) => event.stopPropagation()}>
                <button
                    type="button"
                    className="goldcrest-popup__close"
                    onClick={close}
                    aria-label="Close banner"
                >
                    &times;
                </button>
                <picture>
                    <source media="(min-width: 769px)" srcSet={desktopImage} width="1600" height="900" />
                    <img
                        className="goldcrest-popup__image"
                        src={mobileImage}
                        width="720"
                        height="1280"
                        decoding="async"
                        alt="Goldcrest Views Community - Pakistan's premium high-rise community in DHA Phase II, Islamabad"
                    />
                </picture>
                <Link
                    to="/project/goldcrest-views"
                    className="goldcrest-popup__btn"
                    onClick={close}
                >
                    Click for Inquiry
                </Link>
            </div>
        </div>
    );
};

export default GoldcrestBanner;
